/* eslint-disable max-lines -- Pre-existing violation */
import {skipToken} from '@reduxjs/toolkit/query';
import {isValid} from 'date-fns';
import {
  AttributesRow,
  Button,
  Dropdown,
  DropdownItem,
  Flag,
  getFilePreviewUrl,
  openConfirmDialog,
} from 'platform/components';
import {Hide, HStack, Link, Show} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {always, isNil, isNotEmpty} from 'ramda';
import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {
  CheckoutCorrectiveTaxDocumentType,
  useGetBankListQuery,
  useGetCurrenciesQuery,
  useGetParticipationQuery,
  useGetTenantQuery,
} from '@dms/api/core';
import {OrderResponseBody, useGetInvoicingDocumentPaymentListQuery} from '@dms/api/finance';
import {
  OrderPaymentResponseBody,
  useCancelPurchasePaymentMutation,
  useCancelSaleBalancePaymentMutation,
  useCancelSaleDepositPaymentMutation,
  useDeletePaymentFileMutation,
  useGetBusinessCaseQuery,
  useGetCheckoutV2Query,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {accountingRoutes, testIds} from '@dms/routes';
import {
  EMPTY_PLACEHOLDER,
  handleApiError,
  hasAclDocsWithPermission,
  useBank,
  useDownloadExternalPrintout,
  usePermissions,
} from '@dms/shared';

import {
  buildArray,
  composePath,
  getApiDateString,
  isNilOrZero,
  isNotNilOrEmpty,
  Nullish,
  openFile,
  openInNewTab,
  parseDate,
  useNavigate,
  useRequiredParams,
} from 'shared/web';

import {useAccountingOperation} from '../../../hooks/useAccountingOperation';
import {getAccountingProcessFlagProps} from '../../../utils/getAccountingProcessFlagProps';
import {isPaymentAccountedExternally} from '../../../utils/isPaymentAccountedExternally';
import {openCheckoutMessageLogDialog} from '../utils/openCheckoutMessageLogDialog';
import {useCreateCheckoutCorrectiveTaxDocumentDirectly} from './useCreateCheckoutCorrectiveTaxDocumentDirectly';
import {useIsStandardInvoiceDisabled} from './useIsStandardInvoiceDisabled';

type GetPaymentDetailAttributesArgs = {
  payment: OrderPaymentResponseBody;
  order: OrderResponseBody;
  checkoutId: string;
  refreshBusinessCaseCheckoutInfo: (customerId: string | Nullish) => void;
};

const MARGIN_INVOICE_TAG = 'm_';

// eslint-disable-next-line complexity, max-lines-per-function, max-statements -- Pre-existing violation
export const useGetPaymentDetailAttributes = ({
  checkoutId,
  order,
  payment,
  refreshBusinessCaseCheckoutInfo,
}: GetPaymentDetailAttributesArgs) => {
  const {id: businessCaseId} = useRequiredParams();

  const [shouldHideStandardInvoice] = useIsStandardInvoiceDisabled(order, businessCaseId);

  const [cancelPurchasePayment, {isLoading: isCancelPurchasePaymentMutationLoading}] =
    useCancelPurchasePaymentMutation();
  const [cancelSaleDepositPayment, {isLoading: isCancelSaleDepositPaymentMutationLoading}] =
    useCancelSaleDepositPaymentMutation();
  const [cancelSaleBalancePayment, {isLoading: isCancelSaleBalancePaymentMutationLoading}] =
    useCancelSaleBalancePaymentMutation();

  const isCancelPaymentLoading =
    isCancelPurchasePaymentMutationLoading ||
    isCancelSaleDepositPaymentMutationLoading ||
    isCancelSaleBalancePaymentMutationLoading;

  const [deletePaymentFile, {isLoading: isDeletingPaymentFile, originalArgs}] =
    useDeletePaymentFileMutation();
  const {data: tenant} = useGetTenantQuery();
  const {data: currencies} = useGetCurrenciesQuery();
  const {data: selectBankLists} = useGetBankListQuery({countryCode: tenant?.country});
  const {composeBankAccount} = useBank();

  const {data: checkout} = useGetCheckoutV2Query({checkoutId});

  const {data: businessCaseParticipation} = useGetParticipationQuery(
    {
      recordId: businessCaseId,
      resourceId: EntityResourceIds.businessCase,
    },
    {skip: isNil(businessCaseId)}
  );

  const {data: businessCase} = useGetBusinessCaseQuery({businessCaseId});

  const isBrokerageInternalDocument =
    businessCase?.businessCaseInternalType === 'PURCHASE_BROKERAGE' ||
    !!businessCase?.brokerageBusinessCaseId;

  const isDeductibleVehicle =
    (order.orderDiscriminator === 'SALE' && order.items.some((item) => item.deductible)) ||
    checkout?.orders.some(
      (order) => order.orderDiscriminator === 'PURCHASE_BROKERAGE_SALE' && order.items[0].deductible
    );

  const [
    canCancelSellingInvoice,
    canIssueSellingCorrectiveTaxDocument,
    canReadDocuments,
    canReadInvoice,
    canReadInvoiceDocument,
    canReadTaxDocumentForPayment,
    canReadInvoiceProformaDocument,
    canReadBalanceInvoiceDocument,
    canReadBrokerageInternalInvoiceDocument,
    canReadBrokerageInternalInvoiceDocumentWithVat,
    canReadInternalInvoiceDocument,
    canReadInternalInvoiceDocumentWithVat,
    _canReadInternalCorrectiveTaxDocument,
    canCreateCheckoutDocuments,
    canCreateExternalCheckoutDocuments,
    canCreateInvoice,
  ] = usePermissions({
    permissionKeys: [
      'cancelBusinessCaseSellingInvoice',
      'issueBusinessCaseSellingCorrection',
      'readDocuments',
      'readInvoice',
      'readInvoiceDocument',
      'readTaxDocumentForPayment',
      'readInvoiceProformaDocument',
      'readBalanceInvoiceDocument',
      'readBrokerageInternalInvoiceDocument',
      'readBrokerageInternalInvoiceDocumentWithVat',
      'readInternalInvoiceDocument',
      'readInternalInvoiceDocumentWithVat',
      'readInternalCorrectiveTaxDocument',
      'createCheckoutDocuments',
      'createExternalCheckoutDocuments',
      'createInvoice',
    ],
    scopes: {
      cancelBusinessCaseSellingInvoice: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      issueBusinessCaseSellingCorrection: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      readInvoiceDocument: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      readTaxDocumentForPayment: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      readInvoiceProformaDocument: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      readBalanceInvoiceDocument: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      readBrokerageInternalInvoiceDocument: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      readBrokerageInternalInvoiceDocumentWithVat: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      readInternalInvoiceDocument: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      readInternalInvoiceDocumentWithVat: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      readInternalCorrectiveTaxDocument: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
    },
  });

  const isAccountedExternally = isPaymentAccountedExternally(
    canCreateExternalCheckoutDocuments,
    payment.paymentDiscriminator
  );
  const {data: accountingOperation} = useAccountingOperation(
    isAccountedExternally ? {checkoutId, orderId: order.id, paymentId: payment.id} : skipToken
  );
  const accountingProcess = accountingOperation?.accountingProcess;
  const targetUrl = accountingProcess?.request.targetUrl;
  const validTargetUrl = isAccountedExternally && isNotNilOrEmpty(targetUrl) ? targetUrl : null;
  const printout = accountingProcess?.printout;
  const printoutFileId = printout?.state === 'available' ? printout.fileId : null;
  const [
    downloadExternalPrintout,
    {isDownloading: isDownloadingExternalPrintout, originalFilename: printoutOriginalFilename},
  ] = useDownloadExternalPrintout(printoutFileId);

  const isInternalDocumentReadable = match([isBrokerageInternalDocument, isDeductibleVehicle])
    .with(
      [true, true],
      always(hasAclDocsWithPermission(canReadBrokerageInternalInvoiceDocumentWithVat))
    )
    .with([true, false], always(hasAclDocsWithPermission(canReadBrokerageInternalInvoiceDocument)))
    .with([false, true], always(hasAclDocsWithPermission(canReadInternalInvoiceDocumentWithVat)))
    .with([false, false], always(hasAclDocsWithPermission(canReadInternalInvoiceDocument)))
    .otherwise(always(true));

  const isCancelInvoiceButtonDisabled =
    order.orderDiscriminator === 'PURCHASE' ||
    order.orderDiscriminator === 'PURCHASE_BROKERAGE_SALE' ||
    order.orderDiscriminator === 'PURCHASE_BROKERAGE_FEES'
      ? false
      : !canCancelSellingInvoice;

  const customerBankAccounts = order.contractInformation.customerContractInformation.bankAccounts;

  // `ACCOUNTING.CHECKOUT.EXTERNAL` (`canCreateExternalCheckoutDocuments`) is a standalone way to
  // create a corrective tax document, independent of `ACCOUNTING.INVOICE.WRITE`
  // (`canCreateInvoice`) — a tenant using external checkout for their invoicing typically has no
  // need for (and may not have) the internal invoice-write permission. The action should hide
  // only when the user has neither route available (T20-140144: a tenant with external checkout
  // enabled but not invoice-write was incorrectly shown a permanently-disabled action).
  const isIssueCorrectiveTaxDocumentHidden =
    (!canCreateInvoice && !canCreateExternalCheckoutDocuments) ||
    (order.orderDiscriminator === 'PURCHASE' ||
    order.orderDiscriminator === 'PURCHASE_BROKERAGE_SALE' ||
    order.orderDiscriminator === 'PURCHASE_BROKERAGE_FEES'
      ? false
      : !(canIssueSellingCorrectiveTaxDocument && canReadInvoice));

  const {data: proformaPayments} = useGetInvoicingDocumentPaymentListQuery(
    {
      invoicingDocumentType: 'invoice_proforma',
      invoicingDocumentId: payment.proformaInvoiceId ?? '',
    },
    {skip: isNil(payment.proformaInvoiceId)}
  );

  const {data: invoicePayments} = useGetInvoicingDocumentPaymentListQuery(
    {
      invoicingDocumentType: 'invoice',
      invoicingDocumentId: payment.invoiceId ?? '',
    },
    {skip: isNil(payment.invoiceId)}
  );

  const activeProformaPayments = proformaPayments?.filter(
    (item) => isNil(item.cancelingTransactionId) && isNil(item.cancelledTransactionId)
  );
  const activeInvoicePayments = invoicePayments?.filter(
    (item) => isNil(item.cancelingTransactionId) && isNil(item.cancelledTransactionId)
  );

  const arePaymentsEmpty =
    isNilOrEmpty(activeProformaPayments) && isNilOrEmpty(activeInvoicePayments);

  const navigate = useNavigate();

  const {
    id: correctiveTaxDocumentInvoicingDocumentId,
    invoiceType: correctiveTaxDocumentInvoiceType,
  } = match([
    isNotEmpty(payment.taxableDocuments),
    isNotEmpty(payment.proformaInvoices),
    order.depositAllowed,
  ])
    .with(
      [true, Pattern.boolean, Pattern.boolean],
      always({id: payment.taxDocumentId, invoiceType: 'TAX_DOCUMENT_FOR_PAYMENT'})
    )
    .with(
      [Pattern.boolean, true, Pattern.boolean],
      always({id: payment.proformaInvoiceId, invoiceType: 'PROFORMA'})
    )
    .with([false, false, true], always({id: payment.invoiceId, invoiceType: 'BALANCE_INVOICE'}))
    .otherwise(always({id: payment.invoiceId, invoiceType: 'ISSUED'}));

  const [
    createCorrectiveTaxDocumentDirectly,
    {
      isLoading: isCreatingCorrectiveTaxDocumentDirectly,
      isReady: isCorrectiveTaxDocumentDirectlyReady,
    },
  ] = useCreateCheckoutCorrectiveTaxDocumentDirectly({
    businessCaseId,
    orderId: order.id,
    checkoutId,
    paymentId: payment.id,
    invoicingDocumentId: correctiveTaxDocumentInvoicingDocumentId,
    invoiceType: correctiveTaxDocumentInvoiceType,
    isSellerAsSupplier: checkout?.sellerAsSupplierEnabled ?? false,
    enabled: canCreateExternalCheckoutDocuments,
  });

  const isCorrectiveTaxDocumentDropdownDisabled =
    isCreatingCorrectiveTaxDocumentDirectly || !isCorrectiveTaxDocumentDirectlyReady;

  if (isNil(tenant) || isNil(currencies) || isNil(selectBankLists)) {
    return [[]];
  }

  const tenantBankAccountCurrency = currencies.find(
    (currency) => currency.code === payment.tenantBankAccount?.currencyCode
  );

  const bankAccount = payment.customerBankAccountId
    ? (customerBankAccounts.find(
        (bankAccount) => bankAccount.id === payment.customerBankAccountId
      ) ?? null)
    : null;

  const paymentMethod = getPaymentMethodOptions().find(
    (option) => option.value === payment.paymentMethod
  );

  const isPaymentStateConcept = payment.paymentState === 'CONCEPT';

  const issuedOnDateParsed = parseDate(payment.issuedOn);
  const dateOfTaxableSupply = payment.dateOfTaxableSupply
    ? getApiDateString(parseDate(payment.dateOfTaxableSupply))
    : null;
  const issuedOn =
    payment.issuedOn && issuedOnDateParsed && isValid(issuedOnDateParsed)
      ? getApiDateString(issuedOnDateParsed)
      : null;
  const dueDateParsed = parseDate(payment.dueDate);
  const dueDate =
    payment.dueDate && dueDateParsed && isValid(dueDateParsed)
      ? getApiDateString(dueDateParsed)
      : null;

  const toggleCreateCorrectiveTaxDocument = (type: CheckoutCorrectiveTaxDocumentType) => () => {
    if (correctiveTaxDocumentInvoicingDocumentId === null) {
      return;
    }

    if (canCreateExternalCheckoutDocuments) {
      createCorrectiveTaxDocumentDirectly(type);
      return;
    }

    navigate(
      composePath(accountingRoutes.newCorrectiveTaxDocument, {
        params: {id: correctiveTaxDocumentInvoicingDocumentId},
        queryParams: {
          invoiceType: correctiveTaxDocumentInvoiceType,
          checkoutId,
          type,
          businessCaseId,
          orderId: order.id,
          paymentId: payment.id,
        },
      })
    );
  };

  const handleDeletePaymentFile = (fileId: string) =>
    deletePaymentFile({fileId, checkoutId, orderId: order.id, paymentId: payment.id})
      .unwrap()
      .catch(handleApiError);

  const handleCancelPayment = () =>
    openConfirmDialog({
      text: i18n.t('page.businessCase.checkout.cancelInvoice'),
      onConfirm: () => {
        const cancelPaymentMutation =
          // eslint-disable-next-line no-nested-ternary
          payment.paymentDiscriminator === 'PURCHASE'
            ? cancelPurchasePayment
            : payment.paymentDiscriminator === 'DEPOSIT'
              ? cancelSaleDepositPayment
              : cancelSaleBalancePayment;
        return cancelPaymentMutation({
          businessCaseId,
          orderId: order.id,
          paymentId: payment.id,
        })
          .unwrap()
          .then(() => refreshBusinessCaseCheckoutInfo(null))
          .catch(handleApiError);
      },
    });

  const rows = buildArray<AttributesRow>()
    .add({
      label: i18n.t('entity.checkout.labels.paymentMethod'),
      value: isPaymentStateConcept ? null : paymentMethod?.label,
      testId: 'paymentMethod',
    })
    .add({
      label: i18n.t('entity.checkout.labels.issuedOn'),
      value: issuedOn,
      testId: 'issuedOn',
    })
    .add({
      label: i18n.t('entity.checkout.labels.dueDate'),
      value: isPaymentStateConcept ? null : dueDate,
      testId: 'dueDate',
    })
    .add({
      label: i18n.t('entity.accounting.labels.dateOfTaxableSupply'),
      value: dateOfTaxableSupply,
      testId: 'dateOfTaxableSupply',
    })
    .when(isNotNil(payment.supplierInvoiceNumber), {
      label: i18n.t('entity.checkout.labels.supplierInvoiceNumber'),
      value: payment.supplierInvoiceNumber || null,
      testId: 'supplierInvoiceNumber',
    })
    .when(payment.paymentState !== 'CONCEPT' && payment.paymentMethod !== 'CASH', {
      label: i18n.t('general.labels.bankAccount'),
      value: bankAccount // eslint-disable-line no-nested-ternary -- Pre-existing violation
        ? composeBankAccount(bankAccount.bankAccountData)
        : payment.tenantBankAccount
          ? [
              payment.tenantBankAccount?.accountName,
              payment.tenantBankAccount?.accountNumber,
              payment.tenantBankAccount?.bankName,
              tenantBankAccountCurrency?.name,
            ]
              .filter(Boolean)
              .join(', ')
          : null,
      testId: 'bankAccount',
    })
    .when(
      order.orderDiscriminator !== 'PURCHASE' &&
        order.orderDiscriminator !== 'PURCHASE_BROKERAGE_SALE',
      {
        label: i18n.t('entity.checkout.labels.note'),
        value: payment.comment || EMPTY_PLACEHOLDER,
        testId: 'note',
      }
    )
    .when(isNotNil(accountingProcess), {
      label: i18n.t('entity.checkout.labels.accountingState'),
      // eslint-disable-next-line eag/prefer-show -- Pre-existing violation
      content: isNotNil(accountingProcess) ? (
        <Flag
          {...getAccountingProcessFlagProps(accountingProcess)}
          size="small"
          isSubtle
          data-testid={testIds.businessCase.checkout('paymentAttributes-accountingState')}
        />
      ) : null,
      testId: 'accountingState',
    })
    .when(isAccountedExternally, {
      label: i18n.t('entity.checkout.labels.externalMessageLog'),
      content: (
        <Button
          size="small"
          variant="link"
          title={i18n.t('entity.checkout.actions.viewExternalMessageLog')}
          onClick={() => openCheckoutMessageLogDialog(checkoutId)}
          data-testid={testIds.businessCase.checkout('paymentAttributes-externalMessageLog')}
        />
      ),
      testId: 'externalMessageLog',
    })
    .when(isAccountedExternally && printout?.state === 'available', {
      label: i18n.t('entity.checkout.labels.externalPrintout'),
      content: (
        <Button
          size="small"
          variant="link"
          isLoading={isDownloadingExternalPrintout}
          isDisabled={isDownloadingExternalPrintout}
          title={printoutOriginalFilename ?? i18n.t('general.actions.download')}
          onClick={() => downloadExternalPrintout()}
          data-testid={testIds.businessCase.checkout('paymentAttributes-externalPrintout')}
        />
      ),
      testId: 'externalPrintout',
    })
    .whenNotNil(validTargetUrl, (resolvedTargetUrl) => ({
      label: i18n.t('entity.checkout.labels.externalUrl'),
      content: (
        <Button
          size="small"
          variant="link"
          rightIcon="action/open_in_new"
          title={i18n.t('entity.checkout.actions.viewExternalUrl')}
          onClick={() => openInNewTab(resolvedTargetUrl)}
          data-testid={testIds.businessCase.checkout('paymentAttributes-externalUrl')}
        />
      ),
      testId: 'externalUrl',
    }));

  const isPaymentAmountZero = isNilOrZero(Number(payment.amount?.amount ?? '0'));
  const canCancelInvoice =
    (payment.paymentState === 'PENDING' || isPaymentAmountZero) && arePaymentsEmpty;

  rows.push(
    ...payment.invoices.map((invoice) => {
      const isMarginInvoice = invoice.name.startsWith(MARGIN_INVOICE_TAG);
      const hasMarginInvoice = payment.invoices.some(
        (item) => item.name === `${MARGIN_INVOICE_TAG}${invoice.name}`
      );

      const isStandardInvoiceDisabled = shouldHideStandardInvoice && hasMarginInvoice;

      const shouldHideActions = match([
        hasMarginInvoice,
        shouldHideStandardInvoice,
        isMarginInvoice,
      ])
        .with([true, true, false], always(true))
        .with([true, false, false], always(false))
        .with([false, true, true], always(false))
        .with([false, false, true], always(true))
        .with([false, Pattern.boolean, false], always(false))
        .otherwise(always(false));

      return {
        label:
          invoice.documentType === 'PAYMENT_PROFORMA_INVOICE'
            ? i18n.t('entity.checkout.labels.proformaInvoice')
            : i18n.t('entity.checkout.labels.invoice'),
        content: (
          <HStack justify="space-between" wrap>
            <Link
              size="small"
              onClick={() => openFile(invoice.fileUri)}
              title={invoice.name}
              isDisabled={
                isStandardInvoiceDisabled ||
                !canReadDocuments ||
                !(invoice.documentType === 'PAYMENT_PROFORMA_INVOICE'
                  ? hasAclDocsWithPermission(canReadInvoiceProformaDocument)
                  : hasAclDocsWithPermission(canReadInvoiceDocument) ||
                    hasAclDocsWithPermission(canReadBalanceInvoiceDocument))
              }
              data-testid={testIds.businessCase.checkout('paymentAttributes-invoice')}
            />
            <Hide when={shouldHideActions}>
              <HStack spacing={2} align="center">
                <Show when={canCancelInvoice && canCreateCheckoutDocuments}>
                  <Button
                    size="small"
                    isLoading={isCancelPaymentLoading}
                    variant="dangerLink"
                    title={i18n.t('entity.invoice.actions.cancelInvoice')}
                    isDisabled={isCancelInvoiceButtonDisabled}
                    onClick={handleCancelPayment}
                    data-testid={testIds.businessCase.checkout('paymentAttributes-cancelInvoice')}
                  />
                </Show>

                <Show when={isIssueCorrectiveTaxDocumentHidden}>
                  <Link
                    isDisabled
                    size="small"
                    title={i18n.t('entity.checkout.actions.correctiveTaxDocument')}
                    data-testid={testIds.businessCase.checkout(
                      'paymentAttributes-correctiveDropdown'
                    )}
                  />
                </Show>
                <Hide when={isIssueCorrectiveTaxDocumentHidden}>
                  <Dropdown
                    closeOnBlur
                    key={invoice.fileUri}
                    data-testid={testIds.businessCase.checkout(
                      'paymentAttributes-correctiveDropdown'
                    )}
                    dropdownControl={
                      <Link
                        size="small"
                        title={i18n.t('entity.checkout.actions.correctiveTaxDocument')}
                        // The dropdown auto-closes on click, so a `DropdownItem`'s own
                        // `isDisabled` is invisible to the user by the time it would matter —
                        // the trigger itself must reflect the in-flight/not-ready state so a
                        // click can't be attempted (and silently no-op) in the first place.
                        isDisabled={isCorrectiveTaxDocumentDropdownDisabled}
                        data-testid={testIds.businessCase.checkout(
                          'paymentAttributes-correctiveDropdown'
                        )}
                      />
                    }
                  >
                    <DropdownItem
                      onClick={toggleCreateCorrectiveTaxDocument('full')}
                      label={i18n.t('entity.accounting.labels.correctiveTaxDocumentFull')}
                      isDisabled={isCorrectiveTaxDocumentDropdownDisabled}
                      data-testid={testIds.businessCase.checkout(
                        'paymentAttributes-correctiveDropdown-full'
                      )}
                    />
                    <DropdownItem
                      onClick={toggleCreateCorrectiveTaxDocument('partial')}
                      label={i18n.t('entity.accounting.labels.correctiveTaxDocumentPartial')}
                      isDisabled={isCorrectiveTaxDocumentDropdownDisabled}
                      data-testid={testIds.businessCase.checkout(
                        'paymentAttributes-correctiveDropdown-partial'
                      )}
                    />
                  </Dropdown>
                </Hide>
              </HStack>
            </Hide>
          </HStack>
        ),
        testId: isMarginInvoice ? 'marginInvoice' : 'invoice',
      };
    })
  );

  rows.push(
    ...payment.internalInvoiceDocuments.map((internalInvoiceDocument, index) => ({
      label: i18n.t('entity.checkout.labels.internalInvoiceDocument'),
      content: (
        <Link
          size="small"
          isDisabled={!canReadDocuments || !isInternalDocumentReadable}
          onClick={() => {
            // if, for some reason, this getFilePreviewUrl() usage will become problematic, add
            // documentType to `getFilePreviewUrl()` as its second argument
            openFile(getFilePreviewUrl(internalInvoiceDocument.fileUri));
          }}
          title={internalInvoiceDocument.name}
          data-testid={testIds.businessCase.checkout(
            `paymentAttributes-internalInvoice-[${index}]`
          )}
        />
      ),
      testId: 'internalInvoiceDocument',
    }))
  );

  rows.push(
    ...payment.proformaInvoices.map((proformaInvoice, index) => ({
      label: i18n.t('entity.checkout.labels.proformaInvoice'),
      content: (
        <HStack justify="space-between">
          <Link
            size="small"
            onClick={() => openFile(proformaInvoice.fileUri)}
            isDisabled={
              !canReadDocuments || !hasAclDocsWithPermission(canReadInvoiceProformaDocument)
            }
            title={proformaInvoice.name}
            data-testid={testIds.businessCase.checkout(
              `paymentAttributes-proformaInvoice-[${index}]`
            )}
          />
          <Show when={payment.paymentDiscriminator === 'DEPOSIT'}>
            <HStack spacing={2} align="center">
              <Show
                when={
                  payment.paymentState === 'PENDING' &&
                  arePaymentsEmpty &&
                  canCreateCheckoutDocuments
                }
              >
                <Button
                  size="small"
                  isLoading={isCancelPaymentLoading}
                  variant="dangerLink"
                  title={i18n.t('entity.invoice.actions.cancelInvoice')}
                  data-testid={testIds.businessCase.checkout(
                    `paymentAttributes-proformaInvoice-[${index}]-cancel`
                  )}
                  onClick={handleCancelPayment}
                  isDisabled={isCancelInvoiceButtonDisabled || !canReadDocuments}
                />
              </Show>
            </HStack>
          </Show>
        </HStack>
      ),
      testId: 'proformaInvoice',
    }))
  );

  if (payment.paymentDiscriminator !== 'DEPOSIT') {
    rows.push(
      ...payment.taxableDocuments.map((taxDocument, index) => ({
        label: i18n.t('entity.checkout.labels.taxableDocument'),
        content: (
          <Link
            size="small"
            isDisabled={
              !canReadDocuments || !hasAclDocsWithPermission(canReadTaxDocumentForPayment)
            }
            onClick={() => openFile(taxDocument.fileUri)}
            title={taxDocument.name}
            data-testid={testIds.businessCase.checkout(
              `paymentAttributes-taxableDocument-[${index}]`
            )}
          />
        ),
        testId: 'taxableDocument',
      }))
    );
  }

  rows.push(
    ...payment.files.map((file, index) => ({
      label: i18n.t('entity.checkout.labels.acceptedInvoice'),
      content: (
        <HStack justify="space-between">
          <Link
            size="small"
            onClick={() => openFile(file.fileUri)}
            title={file.name}
            isDisabled={!canReadDocuments}
            data-testid={testIds.businessCase.checkout(`paymentAttributes-files-[${index}]`)}
          />
          <Button
            size="small"
            isDisabled={!canReadDocuments}
            isLoading={originalArgs?.fileId === file.fileId && isDeletingPaymentFile}
            variant="dangerLink"
            title={i18n.t('general.actions.cancel')}
            onClick={() => handleDeletePaymentFile(file.fileId)}
            data-testid={testIds.businessCase.checkout(`paymentAttributes-files-[${index}]-cancel`)}
          />
        </HStack>
      ),
      testId: 'file',
    }))
  );

  return [rows];
};

export const getPaymentMethodOptions = () => [
  {
    label: i18n.t('entity.checkout.labels.paymentMethods.bankTransfer'),
    value: 'BANK_TRANSFER',
  },
  {
    label: i18n.t('entity.checkout.labels.paymentMethods.cash'),
    value: 'CASH',
  },
  {
    label: i18n.t('entity.checkout.labels.paymentMethods.card'),
    value: 'PAYMENT_CARD',
  },
  {
    label: i18n.t('entity.invoice.paymentMethod.offset'),
    value: 'OFFSET',
  },
  {
    label: i18n.t('entity.invoice.paymentMethod.cashOnDelivery'),
    value: 'CASH_ON_DELIVERY',
  },
];
